import { useState, useEffect } from 'react'
import { fetchTodayGoals, addGoalProgress, fetchGoals } from '../utils/api'
import type { GoalItem, ProgressEntryType } from '../types/okr'

interface DailyProgressInputProps {
  compact?: boolean
  onSuccess?: () => void
}

const progressEntryTypes: Array<{ value: ProgressEntryType; label: string; icon: string }> = [
  { value: 'meeting', label: '会议纪要', icon: '○' },
  { value: 'document', label: '文档产出', icon: '📄' },
  { value: 'code', label: '代码提交', icon: '💻' },
  { value: 'communication', label: '沟通记录', icon: '💬' },
  { value: 'blocker', label: '阻塞解决', icon: '⚠️' },
  { value: 'milestone', label: '里程碑', icon: '🏆' }
]

export default function DailyProgressInput({ compact = false, onSuccess }: DailyProgressInputProps) {
  const [todayGoals, setTodayGoals] = useState<Array<GoalItem & { hasUpdateToday: boolean; lastUpdateDate: string | null; todayUpdateCount: number }>>([])
  const [allGoals, setAllGoals] = useState<GoalItem[]>([])
  const [selectedGoalId, setSelectedGoalId] = useState<string>('')
  const [content, setContent] = useState('')
  const [type, setType] = useState<ProgressEntryType>('communication')
  const [progressDelta, setProgressDelta] = useState<number>(0)
  const [progressAfter, setProgressAfter] = useState<number | undefined>(undefined)
  const [attachments, setAttachments] = useState<Array<{ name: string; url: string; type: string }>>([])
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [todayData, allData] = await Promise.all([
        fetchTodayGoals(),
        fetchGoals()
      ])
      setTodayGoals(todayData)
      setAllGoals(allData)
      
      // 默认选择第一个今天待更新的目标
      const firstPending = todayData.find(g => !g.hasUpdateToday)
      if (firstPending) {
        setSelectedGoalId(firstPending.id)
        setProgressAfter(firstPending.progress)
      }
    } catch (error) {
      console.error('加载数据失败:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedGoalId || !content.trim()) {
      alert('请选择目标并填写进展内容')
      return
    }

    try {
      setLoading(true)
      const entry = {
        content: content.trim(),
        type,
        progressDelta,
        progressAfter,
        attachments,
        mentionedUserIds
      }

      await addGoalProgress(selectedGoalId, entry)
      
      setSuccess(true)
      setContent('')
      setProgressDelta(0)
      setProgressAfter(undefined)
      setAttachments([])
      setMentionedUserIds([])
      
      if (onSuccess) onSuccess()
      
      // 重新加载今日目标数据
      setTimeout(() => {
        loadData()
        setSuccess(false)
      }, 2000)
    } catch (error) {
      console.error('添加进展失败:', error)
      alert('添加进展失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  const selectedGoal = allGoals.find(g => g.id === selectedGoalId)

  if (compact) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-3">今日进展更新</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">选择目标</label>
          <select
            value={selectedGoalId}
            onChange={(e) => setSelectedGoalId(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          >
            <option value="">请选择目标</option>
            {todayGoals.map(goal => (
              <option key={goal.id} value={goal.id}>
                {goal.title} {goal.hasUpdateToday ? '(今日已更新)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">进展内容</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="简要描述今日进展..."
            className="w-full px-3 py-2 border rounded"
            rows={3}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedGoal && `当前进度: ${selectedGoal.progress}%`}
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedGoalId || !content.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-400"
          >
            {loading ? '提交中...' : '提交进展'}
          </button>
        </div>

        {success && (
          <div className="mt-3 p-2 bg-green-50 text-green-700 rounded text-sm">
            ✓ 进展已成功添加
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white border rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">✏️ 今日进展更新</h2>
        <div className="text-sm text-gray-600">
          共 {todayGoals.filter(g => !g.hasUpdateToday).length} 个目标待更新
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2">选择目标</label>
            <select
              value={selectedGoalId}
              onChange={(e) => {
                setSelectedGoalId(e.target.value)
                const goal = allGoals.find(g => g.id === e.target.value)
                if (goal) setProgressAfter(goal.progress)
              }}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">请选择目标</option>
              {todayGoals.map(goal => (
                <option key={goal.id} value={goal.id} className={goal.hasUpdateToday ? 'text-gray-400' : ''}>
                  {goal.title} ({goal.period}) - {goal.owner}
                  {goal.hasUpdateToday ? ' (今日已更新)' : ''}
                </option>
              ))}
            </select>
            
            {selectedGoal && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">当前进度</span>
                  <span className="font-medium">{selectedGoal.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${selectedGoal.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">进展类型</label>
            <div className="grid grid-cols-3 gap-2">
              {progressEntryTypes.map(item => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setType(item.value)}
                  className={`px-3 py-2 border rounded text-center ${type === item.value ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-300'}`}
                >
                  <div className="text-lg mb-1">{item.icon}</div>
                  <div className="text-xs">{item.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">进展内容</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="详细描述今日进展，可以包括会议纪要、关键决策、技术实现等..."
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={4}
          />
          <div className="text-xs text-gray-500 mt-1">
            支持简单Markdown格式，如 **加粗**、- 列表
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">进度调整</label>
            <div className="text-sm">
              {progressDelta > 0 ? '增加' : progressDelta < 0 ? '减少' : '不变'} {Math.abs(progressDelta)}%
              {progressAfter !== undefined && ` → ${progressAfter}%`}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="range"
                min="-100"
                max="100"
                value={progressDelta}
                onChange={(e) => {
                  const delta = parseInt(e.target.value)
                  setProgressDelta(delta)
                  if (selectedGoal) {
                    setProgressAfter(Math.max(0, Math.min(100, selectedGoal.progress + delta)))
                  }
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>-100%</span>
                <span>0%</span>
                <span>+100%</span>
              </div>
            </div>
            
            <div className="w-24">
              <input
                type="number"
                min="0"
                max="100"
                value={progressAfter || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value)
                  setProgressAfter(value)
                  if (value !== undefined && selectedGoal) {
                    setProgressDelta(value - selectedGoal.progress)
                  }
                }}
                className="w-full px-3 py-2 border rounded text-center"
                placeholder="目标进度"
              />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">提及同事（可选）</label>
          <input
            type="text"
            value={mentionedUserIds.join(', ')}
            onChange={(e) => setMentionedUserIds(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            placeholder="输入同事的企微ID，用逗号分隔"
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedGoal && (
              <>
                最后更新: {selectedGoal.lastUpdateDate ? new Date(selectedGoal.lastUpdateDate).toLocaleDateString('zh-CN') : '暂无'}
                {(selectedGoal.todayUpdateCount ?? 0) > 0 && ` (今日已更新 ${selectedGoal.todayUpdateCount ?? 0} 次)`}
              </>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setContent('')
                setProgressDelta(0)
                setProgressAfter(selectedGoal?.progress)
                setAttachments([])
              }}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              重置
            </button>
            <button
              type="submit"
              disabled={loading || !selectedGoalId || !content.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? '提交中...' : '提交进展'}
            </button>
          </div>
        </div>
      </form>

      {success && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg animate-fade-in">
          <div className="flex items-center text-green-700">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium">进展已成功添加</div>
              <div className="text-sm mt-1">目标进度已更新，相关同事已收到通知</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}